import React, { useRef } from 'react';
import { useObservable } from 'react-use-observable';

import { FilterOutlined } from '@ant-design/icons';
import { Button, Col, Row, Spin } from 'antd';

import isAfter from 'date-fns/isAfter';
import startOfDay from 'date-fns/startOfDay';
import subDays from 'date-fns/subDays';
import type { FieldProps } from 'formik';
import { Field, Form as FormikForm, useFormikContext } from 'formik';
import { SubscriptionCancelReasonType } from 'helpers/enums';
import { DateRangePickerFormField } from 'v2/components/Shared/FormFields/DateRangePicker';
import { SelectFormField } from 'v2/components/Shared/FormFields/SelectFormField';
import type { ISelectProductMultipleHandles } from 'v2/components/Shared/FormFields/SelectProduct';
import { SelectProduct } from 'v2/components/Shared/FormFields/SelectProduct';
import { TextFormField } from 'v2/components/Shared/FormFields/TextFormField';
import type { ICancelFilter } from 'v2/interfaces/reports/cancel';
import { CustomerSearchTypes } from 'v2/interfaces/reports/interruption';
import type { ICancelReason } from 'v2/interfaces/subscriptions';
import { cancelService, initialFilter } from 'v2/services/reports/cancel';
import SubscriptionsService from 'v2/services/subscriptions';

function Form() {
  const [isLoading] = useObservable(() => cancelService.isLoading(), []);
  const [cancelReason] = useObservable(
    () => SubscriptionsService.getSubscriptionCancelReasons(SubscriptionCancelReasonType.CANCELLATION),
    []
  );

  const { isValid, isSubmitting, resetForm, dirty, values, setFieldValue } = useFormikContext<ICancelFilter>();
  const selectProductRef = useRef<ISelectProductMultipleHandles>(null);

  const clientTypes = [
    { label: 'E-mail', value: CustomerSearchTypes.EMAIL },
    { label: 'Nome', value: CustomerSearchTypes.NAME },
    { label: 'CPF/CNPJ', value: CustomerSearchTypes.DOCUMENT }
  ];

  const customerStringOptions = {
    [CustomerSearchTypes.NAME]: { label: 'Nome', placeholder: 'Insira o nome' },
    [CustomerSearchTypes.EMAIL]: { label: 'E-mail', placeholder: 'Insira o e-mail' },
    [CustomerSearchTypes.DOCUMENT]: { label: 'CPF/CNPJ', placeholder: 'Insira o CPF/CNPJ' }
  };

  const onChangeClientType = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFieldValue('client', '');
    setFieldValue('customer_type', e.target.value);
  };

  const isOutsideRange = (day: Date) => isAfter(startOfDay(day), startOfDay(new Date()));

  const handleClean = () => {
    resetForm({ values: initialFilter });
    selectProductRef.current?.unCheckAll();
  };

  const motivesOptions = (cancelReason || []).map((item: ICancelReason) => ({
    label: item.name,
    value: item.id
  }));

  const loadingMotivesOptions = cancelReason === null;
  const customerLabel =
    customerStringOptions[!!values.customer_type ? values.customer_type : CustomerSearchTypes.EMAIL].label;
  const customerPlaceholder =
    customerStringOptions[!!values.customer_type ? values.customer_type : CustomerSearchTypes.EMAIL].placeholder;
  const customerMask = values.customer_type === CustomerSearchTypes.DOCUMENT ? 'document' : null;

  const today = startOfDay(new Date());
  const datePreset = [
    { id: 'today', text: 'Hoje', start: today, end: today },
    { id: 'week', text: 'Últimos 7 dias', start: subDays(today, 7), end: today },
    { id: '2weeks', text: 'Últimos 30 dias', start: subDays(today, 30), end: today },
    { id: 'month', text: 'Últimos 60 dias', start: subDays(today, 60), end: today }
  ];

  const isCleanDisabled = !dirty || isSubmitting || isLoading;
  const isFilterDisabled = !isValid || isSubmitting || isLoading;

  return (
    <FormikForm>
      <div id='cancel-filter' className='mb-4 w-full'>
        <Row gutter={[16, 16]} className='w-full'>
          <Col xs={24} sm={12} xl={7}>
            <Field
              id='date'
              label='Período'
              name='date'
              component={DateRangePickerFormField}
              customPreset={datePreset}
              isOutsideRange={isOutsideRange}
              minDate={subDays(startOfDay(new Date()), 120)}
            />
          </Col>
          <Col xs={24} sm={12} xl={9}>
            <Row gutter={[4, 4]}>
              <Col span={8}>
                <Field
                  id='customer_type'
                  name='customer_type'
                  label='Cliente'
                  options={clientTypes}
                  onChange={onChangeClientType}
                  component={SelectFormField}
                  fullWidth
                />
              </Col>
              <Col span={16}>
                <Field
                  id='customer'
                  name='customer'
                  component={TextFormField}
                  label={customerLabel}
                  placeholder={customerPlaceholder}
                  mask={customerMask}
                  inputProps={{ className: '[border-width:1px_1px_1px_0px]' }}
                  fullWidth
                />
              </Col>
            </Row>
          </Col>
          <Col xs={24} sm={12} md={8} xl={8}>
            <Field id='product_id' name='product_id'>
              {(props: FieldProps) => (
                <SelectProduct
                  single={false}
                  {...props}
                  label='Produtos'
                  endpoint='/reports/sales-filter-products'
                  ref={selectProductRef}
                />
              )}
            </Field>
          </Col>
          <Col xs={24} sm={12} md={8} xl={6}>
            <Field id='id' name='id' label='Contrato' mask='number' component={TextFormField} fullWidth />
          </Col>
          <Col xs={24} sm={12} md={8} xl={5}>
            <Field id='invoice_id' name='invoice_id' label='Fatura' mask='number' component={TextFormField} fullWidth />
          </Col>
          <Col xs={24} sm={12} md={12} xl={5}>
            <Field
              id='reason_id'
              name='reason_id'
              label='Motivo'
              loading={loadingMotivesOptions}
              options={motivesOptions}
              component={SelectFormField}
              multiple
              fullWidth
            />
          </Col>
          <Col xs={12} md={6} xl={4} className='flex items-end md:pt-5'>
            <Button id='clean-filter-button' onClick={handleClean} type='text' disabled={isCleanDisabled} block>
              Limpar
            </Button>
          </Col>
          <Col xs={12} md={6} xl={4} className='flex items-end md:pt-5'>
            <Button
              id='filter-button'
              type='primary'
              disabled={isFilterDisabled}
              htmlType='submit'
              icon={isSubmitting || isLoading ? <Spin size='small' /> : <FilterOutlined className='mr-0 text-sm' />}
              block
            >
              Filtrar
            </Button>
          </Col>
        </Row>
      </div>
    </FormikForm>
  );
}

export default Form;
